import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface AdminOrgRow {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  tier: string;
  status: string;
  members: number;
  suspended: boolean;
  founding_customer: boolean;
  created_at: string;
}

export interface AdminOrgListResponse {
  items: AdminOrgRow[];
  total: number;
}

export interface AdminOrgDetail {
  org: AdminOrgRow;
  members: Array<{
    user_id: string;
    email: string;
    full_name: string | null;
    role: string;
    joined_at: string;
  }>;
  recent_audits: Array<{
    id: string;
    trigger: string;
    status: string;
    overall_score: number | null;
    created_at: string;
  }>;
  recent_pr_checks: Array<{
    id: string;
    repo_full_name: string;
    pr_number: number;
    status: string;
    skipped_reason: string | null;
    created_at: string;
  }>;
  usage_this_period: {
    period: string;
    monitored_prs: number;
    audits_run: number;
    api_calls: number;
  };
}

export function useAdminOrgs(search: string) {
  return useQuery({
    queryKey: ['admin', 'orgs', { search }],
    queryFn: () => api<AdminOrgListResponse>(`/api/admin/orgs?q=${encodeURIComponent(search)}`),
    staleTime: 5_000,
  });
}

export function useAdminOrgDetail(orgId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'org', orgId],
    queryFn: () => api<AdminOrgDetail>(`/api/admin/orgs/${orgId}`),
    enabled: Boolean(orgId),
  });
}

type ActionType = 'suspend' | 'unsuspend' | 'founding-customer' | 'audit';

export function useAdminOrgAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, action }: { orgId: string; action: ActionType }) =>
      api<{ ok: boolean; message: string | null }>(`/api/admin/orgs/${orgId}/${action}`, {
        method: 'POST',
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'org', vars.orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });
}

export interface AdminAccessRequestRow {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string;
}

export interface AdminAccessRequestListResponse {
  items: AdminAccessRequestRow[];
  total: number;
}

export function useAdminAccessRequests() {
  return useQuery({
    queryKey: ['admin', 'access-requests'],
    queryFn: () => api<AdminAccessRequestListResponse>('/api/admin/access-requests'),
    staleTime: 5_000,
  });
}
